/**
 * Tier-2 Advanced display composition (spec 2026-07-04 §7): the edit-mode
 * view over the PURE base definition + the live overlay. DISPLAY ONLY —
 * tombstone-dependency rules, capability existence, and ask-language
 * completeness are the server's (POST /config/validate); this module never
 * re-implements merge semantics, it only decorates the projection with
 * origin/disabled flags and synthesizes added entries for rendering.
 */
import {
  buildCheckpointVMs,
  buildHandoffRuleVM,
  buildSignalVM,
  humanizeCondition,
  projectWorkflowOverview,
  type AgendaItemVM,
  type AutomationVM,
  type CheckpointVM,
  type HandoffMessageVM,
  type HandoffRuleVM,
  type MessageVM,
  type SignalVM,
} from '@/utils/workflowOverview'
import type {
  CheckpointSpec,
  EscalationTriggerJson,
  NeedSpec,
  OrderSlotSpec,
  OrderWorkflowDefinitionJson,
  ProcessWorkflowDefinitionJson,
  SignalSpecJson,
} from '@/types/workflowDefinition'
import type { ConfigFinding, WorkflowOverlayJson } from '@/types/workflowConfig'

export interface ConfigAgendaItemVM extends AgendaItemVM {
  origin: 'base' | 'added'
  disabled: boolean
  /** overlay-overridden ask_priority when set, else the base priority —
   * what the list is sorted by. `askPriority` stays the BASE value so
   * editors keep their reset baseline. */
  effectivePriority: number
}
export interface ConfigAutomationVM extends AutomationVM {
  origin: 'base' | 'added'
  disabled: boolean
}
export interface ConfigHandoffRuleVM extends HandoffRuleVM {
  origin: 'base' | 'added'
  disabled: boolean
}
export interface ConfigSignalVM extends SignalVM {
  origin: 'base' | 'added'
  disabled: boolean
}
export interface ConfigCheckpointVM extends CheckpointVM {
  /** Collectible slots/needs not currently in this checkpoint's requires. */
  addable: { key: string; label: string }[]
}
export interface ConfigureViewVM {
  agenda: ConfigAgendaItemVM[]
  automations: ConfigAutomationVM[]
  checkpoints: ConfigCheckpointVM[]
  messages: MessageVM[]
  handoff: { rules: ConfigHandoffRuleVM[]; messages: HandoffMessageVM[] }
  signals: ConfigSignalVM[]
}

function isTombstone(patch: unknown): boolean {
  return (
    !!patch &&
    typeof patch === 'object' &&
    (patch as { disabled?: unknown }).disabled === true
  )
}

function patchPriority(patch: unknown): number | null {
  if (patch && typeof patch === 'object' && !isTombstone(patch)) {
    const p = (patch as { ask_priority?: unknown }).ask_priority
    if (typeof p === 'number') return p
  }
  return null
}

/**
 * Composes the checkpoints projection from the PURE base + the live
 * overlay: base checkpoints minus overlay-tombstoned ones, per-language
 * template text overrides from `overlay.templates`, and checkpoint field
 * overrides (e.g. `requires`) from `overlay.checkpoints`. Reuses the exact
 * VM-building derivation from the Tier-1 overview projection
 * (`buildCheckpointVMs`) — this function only composes the DISPLAY inputs,
 * it never re-implements merge semantics.
 */
function projectCheckpoints(
  base: OrderWorkflowDefinitionJson,
  overlay: WorkflowOverlayJson,
  labelFor: (key: string) => string,
): CheckpointVM[] {
  const baseCheckpoints = base.checkpoints ?? {}
  const tombstoned = new Set<string>([
    ...tombstonedKeys(overlay.slots),
    ...tombstonedKeys(overlay.needs),
  ])
  const composedCheckpoints: Record<string, CheckpointSpec> = {}
  for (const [key, cp] of Object.entries(baseCheckpoints)) {
    const patch = overlay.checkpoints?.[key]
    if (isTombstone(patch)) continue
    const merged = { ...cp, ...(patch as Partial<CheckpointSpec> | undefined) }
    merged.requires = (merged.requires ?? []).filter((r) => !tombstoned.has(r))
    composedCheckpoints[key] = merged
  }

  const baseTemplates = base.templates ?? {}
  const composedTemplates: Record<string, Record<string, string>> = {}
  for (const [key, text] of Object.entries(baseTemplates)) {
    composedTemplates[key] = { ...text, ...(overlay.templates?.[key] ?? {}) }
  }

  return buildCheckpointVMs(composedCheckpoints, composedTemplates, labelFor)
}

function tombstonedKeys(section: Record<string, unknown> | undefined): string[] {
  return Object.entries(section ?? {})
    .filter(([, patch]) => isTombstone(patch))
    .map(([key]) => key)
}

function isDict(v: unknown): v is Record<string, unknown> {
  return !!v && typeof v === 'object' && !Array.isArray(v)
}

/**
 * DISPLAY-ONLY guard against a malformed `match` composing onto the
 * trigger: a raw-JSON-authored draft can carry a non-array per-language
 * value (`{en: "box lunch"}`) or a wholly non-dict `match` (`"garbage"`).
 * Either shape flows untyped into `HandoffRuleVM.keywords` and crashes
 * the `.join()` renderers (Handoff tab list + keyword editor). Drops
 * non-array languages and, if nothing survives, the whole map — the
 * overlay itself is never touched, only this display projection; the
 * server's validate findings surface the malformed value inline.
 */
function sanitizeMatch(match: unknown): Record<string, string[]> | null {
  if (!isDict(match)) return null
  const out: Record<string, string[]> = {}
  for (const [lang, phrases] of Object.entries(match)) {
    if (Array.isArray(phrases)) out[lang] = phrases as string[]
  }
  return Object.keys(out).length > 0 ? out : null
}

/**
 * Composes the handoff projection from the PURE base + the live overlay's
 * `escalation` section (id-keyed trigger map ⊕ base trigger list;
 * `match.<lang>` REPLACE; messages per-key/per-language). DISPLAY ONLY —
 * mirrors `apply_overlay`'s escalation branch for rendering, semantic
 * truth stays server-side. Malformed patch entries are skipped here but
 * preserved in the overlay (server findings flag them).
 */
function projectHandoff(
  base: OrderWorkflowDefinitionJson,
  overlay: WorkflowOverlayJson,
  labelFor: (key: string) => string,
): { rules: ConfigHandoffRuleVM[]; messages: HandoffMessageVM[] } {
  const baseEsc = base.escalation ?? {}
  const baseTriggers = baseEsc.triggers ?? []
  const trigPatches = overlay.escalation?.triggers ?? {}
  const msgPatches = overlay.escalation?.messages ?? {}

  const composedMessages: Record<string, Record<string, string>> = {}
  for (const [key, text] of Object.entries(baseEsc.messages ?? {})) {
    composedMessages[key] = { ...text }
  }
  for (const [key, patch] of Object.entries(msgPatches)) {
    if (!isDict(patch)) continue
    composedMessages[key] = { ...composedMessages[key], ...patch }
  }

  const rules: ConfigHandoffRuleVM[] = baseTriggers.map((trigger) => {
    const patch = trigger.id != null ? trigPatches[trigger.id] : undefined
    let effective = trigger
    if (isDict(patch)) {
      effective = { ...trigger, ...(patch as Partial<EscalationTriggerJson>) }
      if (isDict(patch.match) && isDict(trigger.match)) {
        effective.match = {
          ...trigger.match,
          ...(patch.match as Record<string, string[]>),
        }
      }
    }
    effective = { ...effective, match: sanitizeMatch(effective.match) }
    return {
      ...buildHandoffRuleVM(effective, composedMessages, labelFor),
      origin: 'base' as const,
      disabled: effective.enabled === false,
    }
  })

  const baseIds = new Set(
    baseTriggers.map((t) => t.id).filter((x): x is string => typeof x === 'string'),
  )
  for (const [id, patch] of Object.entries(trigPatches)) {
    if (baseIds.has(id) || !isDict(patch)) continue
    const spec = patch as Partial<EscalationTriggerJson>
    if (typeof spec.kind !== 'string' || typeof spec.when !== 'string') continue
    rules.push({
      ...buildHandoffRuleVM(
        { ...spec, id, kind: spec.kind, when: spec.when, match: sanitizeMatch(spec.match) },
        composedMessages,
        labelFor,
      ),
      origin: 'added',
      disabled: spec.enabled === false,
    })
  }

  return {
    rules,
    messages: Object.entries(composedMessages).map(([key, text]) => ({ key, text })),
  }
}

/**
 * Composes the signals projection from the PURE base + the live overlay's
 * `signals` section (id-keyed patch map ⊕ base signal list; `instruction.<lang>`
 * per-language merge; unmatched id → appended, requiring `on`). DISPLAY ONLY —
 * mirrors `apply_overlay`'s signals branch for rendering; semantic truth stays
 * server-side. Malformed patch entries are skipped here but preserved in the
 * overlay (server findings flag them).
 */
function projectSignals(
  base: OrderWorkflowDefinitionJson,
  overlay: WorkflowOverlayJson,
): ConfigSignalVM[] {
  const baseSignals = base.signals ?? []
  const patches = overlay.signals ?? {}

  const vms: ConfigSignalVM[] = baseSignals.map((sig) => {
    const patch = sig.id != null ? patches[sig.id] : undefined
    let effective = sig
    if (isDict(patch)) {
      effective = { ...sig, ...(patch as Partial<SignalSpecJson>) }
      if (isDict(patch.instruction) && isDict(sig.instruction)) {
        effective.instruction = {
          ...sig.instruction,
          ...(patch.instruction as Record<string, string>),
        }
      }
    }
    return {
      ...buildSignalVM(effective),
      origin: 'base' as const,
      disabled: effective.enabled === false,
    }
  })

  const baseIds = new Set(
    baseSignals.map((s) => s.id).filter((x): x is string => typeof x === 'string'),
  )
  for (const [id, patch] of Object.entries(patches)) {
    if (baseIds.has(id) || !isDict(patch)) continue
    const spec = patch as Partial<SignalSpecJson>
    // An added signal MUST carry `on` (its lifecycle event) — skip otherwise.
    if (typeof spec.on !== 'string') continue
    vms.push({
      ...buildSignalVM({ ...spec, id, on: spec.on, instruction: spec.instruction ?? {} }),
      origin: 'added',
      disabled: spec.enabled === false,
    })
  }

  return vms
}

export function filterFindings(
  findings: ConfigFinding[],
  paths: string[],
): ConfigFinding[] {
  return findings.filter((f) =>
    paths.some((p) => f.path === p || f.path.startsWith(`${p}.`)),
  )
}

export interface OverlayPathDescriptor {
  tab: 'agenda' | 'messages' | 'handoff' | 'signals' | 'overview' | 'steps'
  label: string
  resolved: boolean
}

/** SA-114: the editors' visible field labels — a breadcrumb names what the
 * user sees on the target tab, never the raw overlay grammar. Unknown
 * segments pass through raw. */
const FIELD_LABELS: Record<string, string> = {
  label: 'label',
  type: 'type',
  ask_priority: 'ask order',
  prompt_hint: 'instructions to the AI',
  options: 'choices',
  required_when: 'condition',
  always_required: 'always asked',
  after_turns: 'turns without progress',
  reset_after_minutes: 'quiet minutes before the count resets',
  after: 'occurrences before handoff',
  mode: 'response',
  match: 'phrases',
  message: 'handoff message',
  enabled: 'enabled',
  kind: 'reason for handoff',
  instruction: 'instruction',
  on: 'event',
  stall_force_at: 'forced-ask turns',
  stall_escalate_at: 'handoff turns',
  stall_reset_after_minutes: 'quiet minutes before the stall count resets',
  park_message: 'paused message',
  fallback_reply: 'fallback reply',
  post_completion_cooldown_minutes: 'cooldown after completion (minutes)',
  languages: 'languages',
  on_paused_message: 'message while paused',
  on_post_completion: 'after completion',
  capability: 'capability',
  compensate: 'undo capability',
  template: 'message template',
  requires: 'required before',
  purpose: 'purpose',
}

export function overlayFieldLabel(segment: string): string {
  return FIELD_LABELS[segment] ?? segment
}

/** Entity-label precedence shared with `projectConfigureView`'s labelFor:
 * base slot/need label → overlay slot/need label → raw key. */
export function overlayEntityLabel(
  key: string,
  base?: OrderWorkflowDefinitionJson | null,
  overlay?: WorkflowOverlayJson | null,
): string {
  return (
    base?.slots?.[key]?.label ??
    base?.needs?.[key]?.label ??
    ((overlay?.slots?.[key] as Partial<OrderSlotSpec> | undefined)?.label as
      | string
      | undefined) ??
    ((overlay?.needs?.[key] as Partial<NeedSpec> | undefined)?.label as
      | string
      | undefined) ??
    key
  )
}

/** Field tail after the entity segment: `match.<lang>` folds into
 * "phrases (<lang>)"; everything else maps through the dictionary. */
function fieldTail(segments: string[]): string {
  if (segments.length === 0) return ''
  if (segments[0] === 'match' && segments.length === 2) {
    return ` → ${overlayFieldLabel('match')} (${segments[1]})`
  }
  return segments.map((s) => ` → ${overlayFieldLabel(s)}`).join('')
}

const CONVERSATION_FIELD_SEGMENTS = new Set([
  'stall_force_at',
  'stall_escalate_at',
  'stall_reset_after_minutes',
  'park_message',
  'fallback_reply',
])

function routeTab(path: string): OverlayPathDescriptor['tab'] {
  if (
    path.startsWith('conversation.asks.') ||
    path.startsWith('needs.') ||
    path.startsWith('slots.')
  ) {
    return 'agenda'
  }
  if (path.startsWith('templates.')) return 'messages'
  if (path.startsWith('escalation.')) return 'handoff'
  if (path.startsWith('signals.')) return 'signals'
  // E3 process heads (spec 2026-09-18 §6): all three edit on the Steps tab.
  if (path.startsWith('steps.') || path.startsWith('seats.') || path.startsWith('fields.')) {
    return 'steps'
  }
  return 'overview'
}

/** E3: the editors' visible names for the process overlay's patchable
 * fields (spec §3.1), keyed by the path after the step / seat / field id. */
const PROCESS_FIELD_LABELS: Record<string, string> = {
  'executor.seat': 'seat',
  'executor.form': 'form',
  'executor.instruction': 'instruction',
  'executor.threshold': 'confidence threshold',
  'executor.attachments': 'reads attachments',
  'level.default': 'how it runs',
  'due.after': 'due after',
  'due.calendar': 'due calendar',
  'timeout.after': 'timeout after',
  'timeout.calendar': 'timeout calendar',
  'messages.attention': 'attention-task title',
  'messages.wait': 'wait-task title',
  'messages.guidance': 'guidance',
  title: 'title',
  roles: 'roles',
  label: 'label',
  help: 'help',
}

function processFieldTail(rest: string[]): string {
  if (rest.length === 0) return ''
  const two = rest.slice(0, 2).join('.')
  if (two in PROCESS_FIELD_LABELS) {
    const lang = rest[2] ? ` (${rest[2]})` : ''
    return ` → ${PROCESS_FIELD_LABELS[two]}${lang}`
  }
  const lang = rest[1] ? ` (${rest[1]})` : ''
  return ` → ${PROCESS_FIELD_LABELS[rest[0]] ?? rest[0]}${lang}`
}

/** DOM id of the Steps-tab editor that owns a process overlay path — the
 * step's own card, or the one Seats / Fields block. */
export function processPathAnchor(path: string): string | undefined {
  const [section, id] = path.split('.')
  if (section === 'steps' && id) return `process-step-${id}`
  if (section === 'seats') return 'process-seats'
  if (section === 'fields') return 'process-fields'
  if (section === 'lanes') return 'process-lanes'
  return undefined
}

/**
 * SA-114: single path→label source over the overlay grammar (ruling 1 —
 * extends the SA-84 `findingTarget` seam). Breadcrumb segments use the
 * editors' visible labels; entity labels resolve base-then-overlay; a path
 * outside the recognized grammar comes back raw with `resolved: false`.
 * The first segment names the visible SECTION, which is usually but not
 * always the routed tab (automations findings still route to `overview` —
 * routing is frozen).
 */
export function describeOverlayPath(
  path: string,
  anyBase?: OrderWorkflowDefinitionJson | ProcessWorkflowDefinitionJson | null,
  overlay?: WorkflowOverlayJson | null,
): OverlayPathDescriptor {
  const tab = routeTab(path)
  const parts = path.split('.')
  const raw: OverlayPathDescriptor = { tab, label: path, resolved: false }
  const [section] = parts
  const done = (label: string): OverlayPathDescriptor => ({ tab, label, resolved: true })

  // E3 process heads: a step is named the way its card is (number + title,
  // number from declaration order); an id the base no longer declares (an
  // orphan, spec D14) falls back to the raw id.
  const processBase = anyBase?.kind === 'process' ? anyBase : null
  if (section === 'steps' && parts[1]) {
    const n = processBase ? Object.keys(processBase.steps).indexOf(parts[1]) : -1
    const who = n >= 0 ? `${n + 1} ${processBase!.steps[parts[1]].title?.en ?? parts[1]}` : parts[1]
    return done(`Steps → ${who}${processFieldTail(parts.slice(2))}`)
  }
  if (section === 'seats' && parts[1]) {
    const label = processBase?.seats[parts[1]]?.label?.en ?? parts[1]
    return done(`Steps → seat ${label}${processFieldTail(parts.slice(2))}`)
  }
  if (section === 'fields' && parts[1]) {
    const label = processBase?.fields[parts[1]]?.label?.en ?? parts[1]
    return done(`Steps → field ${label}${processFieldTail(parts.slice(2))}`)
  }
  const base = processBase ? null : (anyBase as OrderWorkflowDefinitionJson | null | undefined)

  if ((section === 'slots' || section === 'needs') && parts[1]) {
    return done(`Agenda → ${overlayEntityLabel(parts[1], base, overlay)}${fieldTail(parts.slice(2))}`)
  }
  if (section === 'conversation') {
    if (parts[1] === 'asks' && parts[2]) {
      const lang = parts[3] ? ` (${parts[3]})` : ''
      return done(`Agenda → ${overlayEntityLabel(parts[2], base, overlay)} → question text${lang}`)
    }
    if (parts.length === 2 && CONVERSATION_FIELD_SEGMENTS.has(parts[1])) {
      return done(`Conversation → ${overlayFieldLabel(parts[1])}`)
    }
    return raw
  }
  if (section === 'templates' && parts[1]) {
    const lang = parts[2] ? ` (${parts[2]})` : ''
    return done(`Messages → ${parts[1]}${lang}`)
  }
  if (section === 'escalation') {
    if (parts[1] === 'triggers' && parts[2]) {
      return done(`Handoff → ${parts[2]} rule${fieldTail(parts.slice(3))}`)
    }
    if (parts[1] === 'messages' && parts[2]) {
      const lang = parts[3] ? ` (${parts[3]})` : ''
      return done(`Handoff → message ${parts[2]}${lang}`)
    }
    return raw
  }
  if (section === 'signals' && parts[1]) {
    return done(`Signals → ${parts[1]}${fieldTail(parts.slice(2))}`)
  }
  if (section === 'checkpoints' && parts[1]) {
    return done(`Overview → checkpoint ${parts[1]}${fieldTail(parts.slice(2))}`)
  }
  if ((section === 'derived' || section === 'side_effects') && parts[1]) {
    return done(`Automations → ${parts[1]}${fieldTail(parts.slice(2))}`)
  }
  if (section === 'settings' && parts[1]) {
    return done(`Settings → ${overlayFieldLabel(parts[1])}`)
  }
  if (section === 'switchable_to' && parts[1]) {
    return done(`Handoff → switch to ${parts[1]}${fieldTail(parts.slice(2))}`)
  }
  return raw
}

/**
 * SA-84 Flow B: maps a finding's overlay-addressed `path` to the Configure
 * tab that surfaces it, so a "Review" affordance on a conflict/carry-forward
 * banner can deep-link straight to the relevant editor. Routing is frozen;
 * since SA-114 it delegates to `describeOverlayPath` so tab routing and
 * labeling can never drift apart.
 */
export function findingTarget(path: string): { tab: OverlayPathDescriptor['tab']; anchor?: string } {
  const anchor = processPathAnchor(path)
  return { tab: describeOverlayPath(path).tab, ...(anchor ? { anchor } : {}) }
}

export function projectConfigureView(
  base: OrderWorkflowDefinitionJson,
  overlay: WorkflowOverlayJson,
): ConfigureViewVM {
  const vm = projectWorkflowOverview(base)
  if (vm.kind !== 'order') return { agenda: [], automations: [], checkpoints: [], messages: [], handoff: { rules: [], messages: [] }, signals: [] }

  const baseSlots = base.slots ?? {}
  const baseNeeds = base.needs ?? {}
  const overlayAsks = overlay.conversation?.asks ?? {}
  const labelFor = (key: string): string => overlayEntityLabel(key, base, overlay)

  const agenda: ConfigAgendaItemVM[] = vm.agenda.map((item) => {
    const section = item.itemKind === 'need' ? 'needs' : 'slots'
    const patch = overlay[section]?.[item.key]
    return {
      ...item,
      origin: 'base',
      disabled: isTombstone(patch),
      effectivePriority: patchPriority(patch) ?? item.askPriority,
    }
  })

  for (const [key, patch] of Object.entries(overlay.slots ?? {})) {
    if (key in baseSlots || isTombstone(patch)) continue
    const spec = patch as Partial<OrderSlotSpec>
    agenda.push({
      key,
      label: (spec.label as string | undefined) ?? key,
      itemKind: 'slot',
      slotType: (spec.type as string | undefined) ?? null,
      askPriority: typeof spec.ask_priority === 'number' ? spec.ask_priority : 100,
      requiredness: spec.always_required
        ? 'always'
        : spec.required_when
          ? 'conditional'
          : 'optional',
      condition: spec.required_when
        ? humanizeCondition(spec.required_when, labelFor)
        : null,
      asks: overlayAsks[key] ?? {},
      promptHint: (spec.prompt_hint as string | undefined) ?? null,
      options: (spec.options as string[] | undefined) ?? null,
      origin: 'added',
      disabled: false,
      effectivePriority:
        typeof spec.ask_priority === 'number' ? spec.ask_priority : 100,
    })
  }

  for (const [key, patch] of Object.entries(overlay.needs ?? {})) {
    // A needs entry colliding with a base OR overlay-added slot key must
    // never synthesize a duplicate row — the slot's own row (base or
    // added, from the loops above) already covers that key.
    if (key in baseNeeds || key in baseSlots || key in (overlay.slots ?? {}) || isTombstone(patch)) {
      continue
    }
    const spec = patch as Partial<NeedSpec>
    const priority = typeof spec.ask_priority === 'number' ? spec.ask_priority : 100
    agenda.push({
      key,
      label: (spec.label as string | undefined) ?? key,
      itemKind: 'need',
      slotType: null,
      askPriority: priority,
      requiredness: 'always',
      condition: null,
      asks: overlayAsks[key] ?? {},
      promptHint: (spec.prompt_hint as string | undefined) ?? null,
      options: null,
      origin: 'added',
      disabled: false,
      effectivePriority: priority,
    })
  }

  agenda.sort(
    (a, b) => a.effectivePriority - b.effectivePriority || a.key.localeCompare(b.key),
  )

  const automations: ConfigAutomationVM[] = vm.automations.map((a) => {
    const section = a.category === 'derived' ? 'derived' : 'side_effects'
    return { ...a, origin: 'base', disabled: isTombstone(overlay[section]?.[a.key]) }
  })
  for (const section of ['derived', 'side_effects'] as const) {
    const baseMap = (base[section] ?? {}) as Record<string, unknown>
    for (const [key, patch] of Object.entries(overlay[section] ?? {})) {
      if (key in baseMap || isTombstone(patch)) continue
      const spec = patch as Record<string, unknown>
      automations.push({
        key,
        capability: (spec.capability as string | undefined) ?? '',
        category: section === 'derived' ? 'derived' : 'side_effect',
        when:
          section === 'derived' && typeof spec.when === 'string'
            ? humanizeCondition(spec.when, labelFor)
            : null,
        dependsOn: ((spec.depends_on as string[] | undefined) ?? []).map(labelFor),
        writes:
          section === 'derived' ? ((spec.writes as string | undefined) ?? null) : null,
        compensate:
          section === 'side_effects'
            ? ((spec.compensate as string | undefined) ?? null)
            : null,
        origin: 'added',
        disabled: false,
      })
    }
  }

  const collectible = [
    ...Object.keys(base.slots ?? {}),
    ...Object.keys(base.needs ?? {}),
  ].filter(
    (k) => !isTombstone(overlay.slots?.[k]) && !isTombstone(overlay.needs?.[k]),
  )
  const checkpoints: ConfigCheckpointVM[] = projectCheckpoints(
    base,
    overlay,
    labelFor,
  ).map((cp) => ({
    ...cp,
    addable: collectible
      .filter((k) => !cp.requireKeys.includes(k))
      .map((k) => ({ key: k, label: labelFor(k) })),
  }))

  return {
    agenda,
    automations,
    checkpoints,
    messages: vm.messages,
    handoff: projectHandoff(base, overlay, labelFor),
    signals: projectSignals(base, overlay),
  }
}
